import { useState } from 'react';
import { findNodeType, type User } from '@loggy/shared';

import { CircleHelp } from 'lucide-react';

import { Alert, Avatar, Button, Card, Heading, Inline, Spinner, Stack, Text, identityTone } from '../design/index.js';
import type { NodeSummary, OrbitItem, Person } from '../api.js';
import { NodeDetail, orbitItemToNodeSummary } from './NodeDetail.js';
import { NodeForm } from './NodeForm.js';
import { Settings } from './Settings.js';
import { NodeGuide } from './NodeGuide.js';
import { useNodes } from './useNodes.js';
import type { NodesState } from './useNodes.js';
import { useOrbit } from './useOrbit.js';
import { usePeople } from './usePeople.js';
import { PersonSelector } from './PersonSelector.js';
import { ShareDialog } from './ShareDialog.js';
import { NotificationMenu } from './NotificationMenu.js';
import { OrbitStage } from './OrbitStage.js';
import { NodeRail } from './NodeRail.js';
import { isMetricOrbitItem } from './OrbitView.js';
import { projectionOverlayClassName } from './SceneAtmosphere.js';
import { GoalForm, type GoalFormNode } from './GoalForm.js';
import {
  navigateToDashboard,
  navigateToGuide,
  navigateToSettings,
  navigateToNode,
  useHashRoute,
} from './useHashRoute.js';
import type { Route } from './useHashRoute.js';
import styles from './Dashboard.module.css';

/**
 * Only one of the goal form and the node form (add or edit) can be open at
 * once — they share this single piece of state so opening either always
 * closes the other, rather than each owning its own boolean that could
 * disagree. Both only ever apply to the caller's OWN dashboard — neither is
 * ever opened while `readOnly` is true, but resetting to `closed` whenever
 * the viewed person changes keeps that true even if the caller had one open
 * and then switched away and back.
 */
type Composing =
  | { kind: 'closed' }
  | { kind: 'goal' }
  /** `node: null` is add mode; a `NodeSummary` prefills rename/delete. */
  | { kind: 'node'; node: NodeSummary | null };

/** Small counts read better as words; past that a numeral is clearer. */
function countWord(n: number): string {
  return ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'][n] ?? String(n);
}

/**
 * What `NodeDetail` renders from when a node is opened. Own nodes come from
 * `useNodes` (which never holds anyone else's); a node reached while viewing
 * someone else's dashboard comes from the orbit item already fetched to draw
 * the ring instead, since a shared node is never in the caller's own node
 * list.
 */
function findOpenNode(
  route: Route,
  readOnly: boolean,
  orbitItems: OrbitItem[],
  nodesState: NodesState,
): NodeSummary | null {
  if (route.name !== 'node') return null;
  if (readOnly) {
    const item = orbitItems.find((candidate) => candidate.nodeId === route.nodeId);
    return item ? orbitItemToNodeSummary(item) : null;
  }
  return nodesState.kind === 'ready'
    ? nodesState.nodes.find((node) => node.id === route.nodeId) ?? null
    : null;
}

/**
 * The signed-in landing page, and the app's router: with just three places to
 * be (the overview, one node's detail, and settings), the hash route from
 * `useHashRoute` decides which of them renders, right here, rather than
 * pulling in a routing library for three forks.
 *
 * The overview is a full-bleed instrument — header across the top, a
 * headline top-left, the orbit's SVG scene filling the centre (`OrbitStage`),
 * a rail of every tracked node down the right (`NodeRail`), and a legend
 * bottom-left — matching `design/Main.dc.html`. `node` and `settings` stay
 * a bounded reading column; a form and a measurement history were never
 * meant to run edge to edge.
 *
 * A `PersonSelector` sits above the orbit once anyone shares nodes with the
 * caller. Selecting a person REPLACES what the orbit shows with theirs —
 * never a merge of the two — and every editing affordance (add, edit, goal,
 * the rail's pencil) is absent from the DOM, not merely disabled, whenever
 * the server says the view is read-only: it refuses those operations anyway,
 * so a control that could only ever error has no reason to render.
 */
export function Dashboard({ user, onSignOut }: { user: User; onSignOut: () => void }) {
  const firstName = user.displayName.split(' ')[0] ?? user.displayName;
  const { state: nodesState, reload: reloadNodes } = useNodes();
  const { state: peopleState, reload: reloadPeople } = usePeople();
  const route = useHashRoute();
  const [composing, setComposing] = useState<Composing>({ kind: 'closed' });
  const [shareOpen, setShareOpen] = useState(false);

  /** The caller's own id selects their own dashboard — the default, and
   *  where selecting yourself in `PersonSelector` returns to. */
  const [selectedUserId, setSelectedUserId] = useState<string>(user.id);

  const selectedPerson =
    peopleState.kind === 'ready'
      ? peopleState.people.find((person) => person.userId === selectedUserId) ?? null
      : null;
  const viewedSpaceId = selectedPerson && !selectedPerson.isSelf ? selectedPerson.spaceId : undefined;
  /** Known the instant a person is picked, before their orbit has even
   *  started loading — used so copy and loading/error labels already read
   *  correctly mid-switch, rather than waiting on the fetch this triggers. */
  const viewingOther = viewedSpaceId !== undefined;

  const { state: orbitState, reload: reloadOrbit } = useOrbit(viewedSpaceId);
  /** The server's own word on whether editing is allowed — confirmed once
   *  the orbit is `ready`, and `viewingOther` until then. In the unshared
   *  case `viewingOther` is always false (nobody else to select), so this is
   *  always false too, exactly as before this feature existed. */
  const readOnly = orbitState.kind === 'ready' ? orbitState.readOnly : viewingOther;

  /** First name of whoever's dashboard is being viewed, for copy that must
   *  say who this data belongs to rather than assuming it is the caller's
   *  own. Only ever read while `readOnly` is true. */
  const subjectFirstName =
    selectedPerson && !selectedPerson.isSelf
      ? selectedPerson.displayName.split(' ')[0] ?? selectedPerson.displayName
      : 'this person';

  const handleSelectPerson = (person: Person) => {
    setSelectedUserId(person.userId);
    // Any open form belongs to the dashboard being left, never the one about
    // to be shown.
    setComposing({ kind: 'closed' });
  };

  /** Accepting an invite is what makes a new person appear in the selector —
   *  the people list cannot notice that on its own — and the caller's own
   *  orbit may itself have changed if the newly accepted invite is what first
   *  gave them a reason to visit this dashboard, so both reload together. */
  const handleInviteAccepted = () => {
    void reloadPeople();
    void reloadOrbit();
  };

  /** Both the orbit and the node list can change from a create, rename or
   *  delete — the orbit because a node's presence/order in it changed, the
   *  node list because `NodeDetail` and the edit form read it. Reloading
   *  only one would leave the other showing stale data. Never called while
   *  `readOnly`, since nothing that triggers it can render then. */
  const reloadAfterNodeChange = () => {
    void reloadOrbit();
    void reloadNodes();
  };

  const orbitItems = orbitState.kind === 'ready' ? orbitState.items : [];

  /**
   * The node holding this dashboard's life events, if it has one.
   *
   * Read from the orbit on screen rather than from the caller's own node list,
   * and that is the whole of the sharing rule: a viewer's orbit contains only
   * the nodes granted to them, so somebody who shared their weight but not
   * their life events simply has no events node here, and the tick box that
   * would reveal them never renders.
   */
  const eventsNodeId =
    orbitItems.find((item) => findNodeType(item.typeKey)?.overlay === 'timeline')?.nodeId ?? null;

  /** The caller's OWN nodes, exactly as `ShareDialog` needs them — never the
   *  currently viewed orbit, which may belong to someone else entirely and
   *  has nothing to do with what the caller themself can share. */
  const ownShareableNodes = nodesState.kind === 'ready' ? nodesState.nodes.map((node) => ({ id: node.id, title: node.title })) : [];

  /**
   * The design's own heading counts what is in orbit rather than greeting by
   * name — the page is about the aspects, and the name is already in the
   * corner. While viewing someone else it says whose aspects they are,
   * instead of assuming they are the caller's own.
   */
  const goalCount = orbitItems.filter((item) => item.goal !== null).length;
  const overdue = orbitItems.filter(
    (item) => item.goal?.progress.daysRemaining !== null && (item.goal?.progress.daysRemaining ?? 1) < 0,
  ).length;

  const orbitHeadline = readOnly
    ? orbitItems.length === 0
      ? `Nothing in orbit for ${subjectFirstName} yet.`
      : goalCount === 0
        ? `${countWord(orbitItems.length)} ${orbitItems.length === 1 ? 'aspect' : 'aspects'} of ${subjectFirstName}, watched`
        : `${countWord(goalCount)} ${goalCount === 1 ? 'aspect' : 'aspects'} of ${subjectFirstName}, in orbit`
    : orbitItems.length === 0
      ? `Welcome, ${firstName}.`
      : goalCount === 0
        ? `${countWord(orbitItems.length)} ${orbitItems.length === 1 ? 'aspect' : 'aspects'} of you, watched`
        : `${countWord(goalCount)} ${goalCount === 1 ? 'aspect' : 'aspects'} of you, in orbit`;

  const orbitSubline = readOnly
    ? orbitItems.length === 0
      ? `${subjectFirstName} hasn't shared anything with you yet.`
      : overdue > 0
        ? `${overdue === 1 ? 'One is' : `${overdue} are`} past ${overdue === 1 ? 'its' : 'their'} deadline.`
        : goalCount === 0
          ? 'Nothing has a target yet.'
          : 'Everything is on its way.'
    : orbitItems.length === 0
      ? 'Add something to track and it will take its place in the ring around you.'
      : overdue > 0
        ? `${overdue === 1 ? 'One is' : `${overdue} are`} past ${overdue === 1 ? 'its' : 'their'} deadline.`
        : goalCount === 0
          ? 'Nothing has a target yet. Set one and it moves inward.'
          : 'Everything is on its way.';

  /**
   * The orbit already carries everything the form needs to offer a choice, so
   * there is no second request and no chance of the two disagreeing about what
   * exists or what it currently reads. Only ever read while composing a goal,
   * which never happens while `readOnly`.
   */
  const goalTargets: GoalFormNode[] = orbitItems.filter(isMetricOrbitItem).map((item) => ({
    id: item.nodeId,
    title: item.title,
    unit: item.unit,
    precision: item.precision,
    latestValue: item.latestValue,
    source: item.source,
  }));

  const openNode = findOpenNode(route, readOnly, orbitItems, nodesState);
  const nodeRouteState = readOnly ? orbitState.kind : nodesState.kind;
  const nodeRouteErrorMessage =
    readOnly
      ? orbitState.kind === 'error' ? orbitState.message : null
      : nodesState.kind === 'error' ? nodesState.message : null;

  /** The open node's active goal, read off the orbit rather than fetched
   *  again — the orbit already carries every node's goal with its progress
   *  computed server-side, so a second request could only disagree with it.
   *  `orbitItems` is already whichever dashboard is currently being viewed,
   *  own or someone else's, so this needs no separate readOnly branch. */
  const openNodeGoal = openNode
    ? orbitItems.find((item) => item.nodeId === openNode.id)?.goal ?? null
    : null;

  const isOverview = route.name !== 'settings' && route.name !== 'node' && route.name !== 'guide';

  return (
    <div className={styles.page}>
      {/* The tell that the overview is projected rather than rendered. Full
          bleed, and only here: the design canvas puts scanlines on the orbit
          screens and never on a reading screen like NodeDetail. */}
      {isOverview && <div className={projectionOverlayClassName} aria-hidden="true" />}
      <header className={styles.bar}>
        {/* The wordmark is the way home, the way it is on most of the web.
            A real button rather than a click handler on a div, so it is
            reachable by keyboard and announced as something you can press. */}
        <button
          type="button"
          className={styles.wordmark}
          onClick={navigateToDashboard}
          data-testid="open-orbit"
        >
          log.gy
        </button>
        <Inline gap={3} align="center">
          {/* Documentation earns a permanent place in the bar: a reader
              needs it precisely when they do not yet know where to look, and
              burying it in settings is where it would never be found. Named
              rather than left as a bare question mark — a glyph asks the
              reader to guess where it goes, and the word is what the page is
              actually called. */}
          <Button
            variant="ghost"
            size="sm"
            onClick={navigateToGuide}
            data-testid="open-guide"
            leading={<CircleHelp size={15} strokeWidth={1.75} aria-hidden="true" />}
          >
            Documentation
          </Button>
          <Button
            variant="secondary"
            size="sm"
            data-testid="open-share"
            onClick={() => setShareOpen(true)}
          >
            Share
          </Button>
          <NotificationMenu onAccepted={handleInviteAccepted} />
          <button
            type="button"
            className={styles.avatarButton}
            onClick={navigateToSettings}
            data-testid="open-settings"
            aria-label={`Settings for ${user.displayName}`}
          >
            <Avatar
              size="md"
              tone={identityTone(user.id)}
              src={user.avatarUrl ?? undefined}
              initials={firstName.slice(0, 1)}
              alt=""
            />
          </button>
        </Inline>
      </header>

      {shareOpen && (
        <ShareDialog
          nodes={ownShareableNodes}
          onClose={() => setShareOpen(false)}
          onSent={() => setShareOpen(false)}
        />
      )}

      <main className={styles.main}>
        {route.name === 'guide' ? (
          <div className={styles.readingColumn}>
            <NodeGuide />
          </div>
        ) : route.name === 'settings' ? (
          <div className={styles.readingColumn}>
            <Settings user={user} onSignOut={onSignOut} />
          </div>
        ) : route.name === 'node' ? (
          <div className={styles.chartColumn}>
            {nodeRouteState === 'loading' && (
              <Card className={styles.next} data-testid="node-detail-loading">
                <Inline gap={3} align="center">
                  <Spinner size="sm" aria-label={readOnly ? 'Loading this dashboard' : 'Loading your nodes'} />
                  <Text size="sm" color="muted">Loading…</Text>
                </Inline>
              </Card>
            )}

            {nodeRouteErrorMessage && (
              <Card className={styles.next} data-testid="node-detail-error">
                <Alert variant="error" title={readOnly ? "Couldn't load this dashboard" : "Couldn't load your nodes"}>
                  {nodeRouteErrorMessage}
                </Alert>
              </Card>
            )}

            {nodeRouteState === 'ready' && openNode && (
              <NodeDetail
                node={openNode}
                goal={openNodeGoal}
                eventsNodeId={eventsNodeId}
                readOnly={readOnly}
                onBack={navigateToDashboard}
              />
            )}

            {nodeRouteState === 'ready' && !openNode && (
              <Card className={styles.next} data-testid="node-detail-not-found">
                <Stack gap={3}>
                  <Text size="sm" color="secondary">That node could not be found.</Text>
                  <Button variant="secondary" size="sm" onClick={navigateToDashboard}>
                    Back to dashboard
                  </Button>
                </Stack>
              </Card>
            )}
          </div>
        ) : (
          <div className={styles.instrument}>
            <div className={styles.headline}>
              <Stack gap={2}>
                <Heading level={1} className={styles.greeting}>
                  {orbitHeadline}
                </Heading>
                <Text color="muted">{orbitSubline}</Text>
              </Stack>

              {peopleState.kind === 'ready' && (
                <PersonSelector
                  people={peopleState.people}
                  selectedUserId={selectedUserId}
                  onSelect={handleSelectPerson}
                  className={styles.personSelector}
                />
              )}
            </div>

            {orbitState.kind === 'loading' && (
              <Card className={styles.stageSlot} data-testid="orbit-loading">
                <Inline gap={3} align="center">
                  <Spinner size="sm" aria-label={readOnly ? 'Loading this dashboard' : 'Loading your orbit'} />
                  <Text size="sm" color="muted">{readOnly ? 'Loading…' : 'Loading your orbit…'}</Text>
                </Inline>
              </Card>
            )}

            {orbitState.kind === 'error' && (
              <Card className={styles.stageSlot} data-testid="orbit-error">
                <Alert variant="error" title={readOnly ? "Couldn't load this dashboard" : "Couldn't load your orbit"}>
                  {orbitState.message}
                </Alert>
              </Card>
            )}

            {orbitState.kind === 'ready' && orbitItems.length === 0 && (
              <div className={styles.orbitGroup} data-testid="orbit-empty">
                <OrbitStage items={orbitItems} onSelect={navigateToNode} className={styles.stage} />

                <div className={styles.railColumn}>
                  <Text size="xs" color="muted" className={styles.label}>In orbit</Text>
                  {readOnly ? (
                    <Card variant="veil" className={styles.emptyInvite} data-testid="orbit-empty-shared">
                      <Stack gap={3}>
                        <Heading level={3} size="lg">Nothing shared yet</Heading>
                        <Text size="sm" color="secondary">
                          {subjectFirstName} hasn't shared anything with you yet.
                        </Text>
                      </Stack>
                    </Card>
                  ) : composing.kind === 'node' ? (
                    <NodeForm
                      node={composing.node}
                      onCancel={() => setComposing({ kind: 'closed' })}
                      onSaved={() => {
                        setComposing({ kind: 'closed' });
                        reloadAfterNodeChange();
                      }}
                      onDeleted={() => {
                        setComposing({ kind: 'closed' });
                        reloadAfterNodeChange();
                      }}
                    />
                  ) : (
                    <Card variant="veil" className={styles.emptyInvite}>
                      <Stack gap={3}>
                        <Heading level={3} size="lg">Nothing in orbit yet</Heading>
                        <Text size="sm" color="secondary">
                          Add something to track and it will take its place in the ring around
                          you. Connect a device afterward, in settings, and it can start filling
                          itself in.
                        </Text>
                        <Button
                          variant="secondary"
                          size="sm"
                          data-testid="orbit-add-node-empty"
                          onClick={() => setComposing({ kind: 'node', node: null })}
                        >
                          Add something to track
                        </Button>
                      </Stack>
                    </Card>
                  )}
                </div>
              </div>
            )}

            {orbitState.kind === 'ready' && orbitItems.length > 0 && (
              <div className={styles.orbitGroup} data-testid="orbit-view">
                <OrbitStage items={orbitItems} onSelect={navigateToNode} className={styles.stage} />

                <div className={styles.railColumn}>
                  <Inline gap={4} align="center" justify="space-between" wrap>
                    <Text size="xs" color="muted" className={styles.label}>In orbit</Text>
                    {!readOnly && composing.kind === 'closed' ? (
                      <Inline gap={2}>
                        <Button
                          variant="secondary"
                          size="sm"
                          data-testid="orbit-add-node"
                          onClick={() => setComposing({ kind: 'node', node: null })}
                        >
                          Add something to track
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          data-testid="orbit-add-goal"
                          onClick={() => setComposing({ kind: 'goal' })}
                        >
                          Set a goal
                        </Button>
                      </Inline>
                    ) : null}
                  </Inline>

                  {!readOnly && composing.kind === 'goal' ? (
                    <GoalForm
                      nodes={goalTargets}
                      onCancel={() => setComposing({ kind: 'closed' })}
                      onSaved={() => {
                        setComposing({ kind: 'closed' });
                        // Re-ask the server: a new goal moves its node off the
                        // watched ring, and which ring it lands on depends on every
                        // other goal's deadline, not just this one.
                        void reloadOrbit();
                      }}
                    />
                  ) : null}

                  {!readOnly && composing.kind === 'node' ? (
                    <NodeForm
                      node={composing.node}
                      onCancel={() => setComposing({ kind: 'closed' })}
                      onSaved={() => {
                        setComposing({ kind: 'closed' });
                        reloadAfterNodeChange();
                      }}
                      onDeleted={() => {
                        setComposing({ kind: 'closed' });
                        reloadAfterNodeChange();
                      }}
                    />
                  ) : null}

                  <NodeRail
                    items={orbitItems}
                    onSelect={navigateToNode}
                    onEdit={
                      readOnly
                        ? undefined
                        : (nodeId) => {
                            const node =
                              nodesState.kind === 'ready'
                                ? nodesState.nodes.find((candidate) => candidate.id === nodeId) ?? null
                                : null;
                            setComposing({ kind: 'node', node });
                          }
                    }
                  />
                </div>
              </div>
            )}

            {orbitState.kind === 'ready' && orbitItems.length > 0 && (
              <div className={styles.legend}>
                <div className={styles.legendLine}>
                  <svg aria-hidden="true" width="26" height="12" viewBox="0 0 26 12" fill="none" className={styles.legendIcon}>
                    <ellipse cx="13" cy="6" rx="12" ry="5" stroke="var(--accent)" strokeWidth="1" opacity=".6" />
                  </svg>
                  <span>Ring &middot; nearest deadline innermost, then open goals, then watched nodes outermost</span>
                </div>
                <div className={styles.legendLine}>
                  <svg aria-hidden="true" width="26" height="12" viewBox="0 0 26 12" fill="none" className={styles.legendIcon}>
                    <circle cx="20" cy="6" r="3.5" fill="var(--accent-bright)" />
                    <path d="M2 6a11 5 0 0 0 18 0" stroke="var(--accent)" strokeWidth="1" opacity=".5" />
                  </svg>
                  <span>Position on the ring &middot; progress toward the target, clockwise from the left; a full lap is achieved</span>
                </div>
                <div className={styles.legendLine}>
                  <svg aria-hidden="true" width="26" height="12" viewBox="0 0 26 12" fill="none" className={styles.legendIcon}>
                    <circle cx="8" cy="6" r="4" fill="var(--accent-bright)" />
                    <circle cx="19" cy="6" r="4" fill="var(--status-overdue)" />
                  </svg>
                  <span>Colour &middot; on track, achieved, or overdue</span>
                </div>
                <div className={styles.legendLine}>
                  <svg aria-hidden="true" width="26" height="12" viewBox="0 0 26 12" fill="none" className={styles.legendIcon}>
                    <circle cx="8" cy="6" r="4" fill="var(--accent-bright)" />
                    <circle cx="19" cy="6" r="4" fill="none" stroke="var(--text-muted)" strokeWidth="2" />
                  </svg>
                  <span>Marker &middot; filled is a goal with a target, hollow is merely watched</span>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
